import type {
  DishTag,
  RichIngredient,
  TaxonomyAllergen,
  TaxonomyPreference,
} from "api-types";
import {
  type FilterReducerActions,
  type MenuFilterState,
  filterMenuReducer,
  userHasSelectedFilters,
} from "menu-filtering";

import {
  PropsWithChildren,
  createContext,
  useContext,
  useMemo,
  useReducer,
} from "react";
import React from "react";

/**
 * Actions that can be performed on the filter state
 * by the user.
 */
export type FilterActions = {
  toggleAllergen: (allergen: TaxonomyAllergen) => void;
  togglePreference: (preference: TaxonomyPreference) => void;
  toggleIngredient: (ingredient: RichIngredient) => void;
  toggleTag: (tag: DishTag) => void;
  toggleShowMayContainAllergens: () => void;
  toggleShowCalories: () => void;
  toggleAllergenDetails: () => void;
  clearAllIngredients: () => void;
  clearAll: () => void;
};

// properties that can be derived from the state
export type DerivedStates = {
  /**
   * If the user has selected any filters
   */
  userHasSelectedFilters: () => boolean;
};

type MenuFilterCtx = MenuFilterState & FilterActions & DerivedStates;
const FilterMenuContext = createContext<MenuFilterCtx | null>(null);

type FilterMenuProviderProps = PropsWithChildren<{
  reducer?: (
    state: MenuFilterState,
    action: FilterReducerActions
  ) => MenuFilterState;
  initialState: MenuFilterState;
  initialActions?: FilterReducerActions[]; // used for testing
}>;

export const FilterMenuProvider = ({
  children,
  reducer = filterMenuReducer,
  initialState,
  initialActions = [],
}: FilterMenuProviderProps) => {
  const [state, dispatch] = useReducer(
    reducer,
    initialActions.reduce(reducer, initialState)
  );

  const menuFilterCtx: MenuFilterCtx = useMemo(() => {
    const filterActions: FilterActions = {
      toggleAllergen: (allergen) =>
        dispatch({ type: "toggleAllergen", allergen }),
      togglePreference: (preference) =>
        dispatch({ type: "togglePreference", preference }),
      toggleIngredient: (ingredient) =>
        dispatch({ type: "toggleIngredient", ingredient }),
      toggleTag: (tag) => dispatch({ type: "toggleTag", tag }),
      toggleShowMayContainAllergens: () =>
        dispatch({ type: "toggleShowMayContainAllergens" }),
      toggleShowCalories: () => dispatch({ type: "toggleShowCalories" }),
      toggleAllergenDetails: () => dispatch({ type: "toggleAllergenDetails" }),
      clearAllIngredients: () => dispatch({ type: "clearAllIngredients" }),
      clearAll: () => dispatch({ type: "clearAll" }),
    };

    const derivedStates: DerivedStates = {
      userHasSelectedFilters: () => userHasSelectedFilters(state),
    };

    return { ...state, ...filterActions, ...derivedStates };
  }, [state, dispatch]);

  return (
    <FilterMenuContext.Provider value={menuFilterCtx}>
      {children}
    </FilterMenuContext.Provider>
  );
};

export const useFilterMenu = (): MenuFilterCtx => {
  const ctx = useContext(FilterMenuContext);

  if (!ctx) {
    throw new Error("useFilterMenu must be used within FilterMenuProvider");
  }

  return ctx;
};
